import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import MultiSelect from "@/components/MultiSelect";
import { LANGUAGES, INTERESTS } from "@/lib/options";

export default function Register() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [f, setF] = useState({
    email: "", password: "", display_name: "", dob: "", gender: "male",
    city: "", bio: "", interested_in: "female",
    languages: [], preferred_languages: [], interests: [], preferred_interests: [],
    accept_terms: false, accept_privacy: false, confirm_18_plus: false,
  });
  const [busy, setBusy] = useState(false);

  const u = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(f);
      toast.success("Account created. Upload at least 2 photos.");
      nav("/onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-dc-gradient flex items-start sm:items-center justify-center p-4 py-10">
      <form onSubmit={submit} className="glass-strong rounded-3xl w-full max-w-lg p-6 sm:p-8">
        <Link to="/" className="font-display text-xl tracking-widest text-white">DILCONNECT</Link>
        <h1 className="font-display mt-4 text-2xl font-extrabold text-white">Create your 18+ account</h1>
        <p className="text-xs text-purple-200/70 mt-1">Server-verified age. Admin approval required.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
          <Field label="Display name"><input data-testid="reg-name" required minLength={2} className="dc-input" value={f.display_name} onChange={u("display_name")} /></Field>
          <Field label="Email"><input data-testid="reg-email" required type="email" className="dc-input" value={f.email} onChange={u("email")} /></Field>
          <Field label="Password"><input data-testid="reg-password" required type="password" minLength={6} className="dc-input" value={f.password} onChange={u("password")} /></Field>
          <Field label="Date of birth"><input data-testid="reg-dob" required type="date" className="dc-input" value={f.dob} onChange={u("dob")} /></Field>
          <Field label="Gender">
            <select data-testid="reg-gender" className="dc-input" value={f.gender} onChange={u("gender")}>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </Field>
          <Field label="Interested in">
            <select data-testid="reg-interested" className="dc-input" value={f.interested_in} onChange={u("interested_in")}>
              <option value="female">Female</option>
              <option value="male">Male</option>
              <option value="any">Any</option>
            </select>
          </Field>
          <Field label="City" wide><input data-testid="reg-city" required className="dc-input" value={f.city} onChange={u("city")} /></Field>
          <Field label="Short bio (optional)" wide>
            <textarea data-testid="reg-bio" maxLength={400} rows={3} className="dc-input" value={f.bio} onChange={u("bio")} />
          </Field>
        </div>

        <div className="mt-5 space-y-4">
          <MultiSelect label="Languages you speak" options={LANGUAGES} value={f.languages} onChange={(v) => setF({ ...f, languages: v })} testPrefix="reg-lang" />
          <MultiSelect label="Preferred languages for communication" options={LANGUAGES} value={f.preferred_languages} onChange={(v) => setF({ ...f, preferred_languages: v })} testPrefix="reg-prefLang" />
          <MultiSelect label="Hobbies & interests" options={INTERESTS} value={f.interests} onChange={(v) => setF({ ...f, interests: v })} testPrefix="reg-int" />
          <MultiSelect label="Preferred hobbies/interests in a connection" options={INTERESTS} value={f.preferred_interests} onChange={(v) => setF({ ...f, preferred_interests: v })} testPrefix="reg-prefInt" />
        </div>

        <div className="mt-5 space-y-2 text-sm text-purple-200/90">
          <label className="flex items-start gap-2"><input data-testid="reg-18" type="checkbox" required checked={f.confirm_18_plus} onChange={u("confirm_18_plus")} /> I confirm I am 18 or older.</label>
          <label className="flex items-start gap-2"><input data-testid="reg-terms" type="checkbox" required checked={f.accept_terms} onChange={u("accept_terms")} /> I accept the <Link to="/terms" className="underline">Terms</Link>.</label>
          <label className="flex items-start gap-2"><input data-testid="reg-privacy" type="checkbox" required checked={f.accept_privacy} onChange={u("accept_privacy")} /> I accept the <Link to="/privacy" className="underline">Privacy Policy</Link>.</label>
        </div>

        <button data-testid="reg-submit" disabled={busy} className="btn-cta mt-6 w-full py-3 rounded-2xl">
          {busy ? "Creating…" : "Create account"}
        </button>
        <div className="mt-4 text-xs text-purple-200/70 text-center">
          Already have an account? <Link to="/login" className="text-white font-semibold">Log in</Link>
        </div>
      </form>
      <style>{`
        .dc-input{width:100%;background:rgba(10,4,20,0.6);border:1px solid rgba(168,85,247,0.3);border-radius:12px;padding:10px 12px;font-size:14px;outline:none}
        .dc-input:focus{border-color:#c084fc;box-shadow:0 0 0 3px rgba(192,132,252,0.2)}
      `}</style>
    </div>
  );
}

const Field = ({ label, children, wide }) => (
  <label className={`block ${wide ? "sm:col-span-2" : ""}`}>
    <span className="text-[11px] uppercase tracking-widest text-purple-200/70">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
